import { useEffect, useRef, useState } from 'react'
import { api, type Hochgeladen, type VerwMandant } from '../api'
import { anzahl, datumKurz } from '../format'
import { Tipp } from '../komponenten/Hinweis'

/* Das Admin-Backoffice.
 *
 * Drei Dinge, und mehr soll es vorerst nicht sein:
 *
 *   1. **Welcher Kunde.** Ein Verwalter gehört zu keinem Mandanten; er
 *      wählt einen und arbeitet dann darin — unter demselben Kontext
 *      und derselben Zeilentrennung wie der Kunde selbst.
 *   2. **Was gebucht ist.** Reporting, Audit, Personenbezug, und die
 *      Nachweise, ohne die ein Mandant nicht aktiv werden darf.
 *   3. **Stammdaten einspielen.** Die Artikelliste aus dem Backoffice
 *      des Kunden, ohne Eingabeaufforderung auf dem Server.
 *
 * Was hier bewusst **nicht** steht: eine einzige fachliche Zeile. Kein
 * Umsatz, kein Artikel, kein Beschäftigter. Die Verwaltung führt in den
 * Flur, nicht in die Zimmer — die Auswertungen sieht der Verwalter
 * genau dort, wo der Kunde sie sieht, nachdem er den Mandanten gewählt
 * hat.
 */

function Zustand({ m }: { m: VerwMandant }) {
  const fehlt: string[] = []
  if (!m.dpa_signed_at) fehlt.push('Auftragsverarbeitung')
  if (m.module_audit && !m.dimension_employee) fehlt.push('Bedienerdimension')

  if (m.status === 'ACTIVE' && !fehlt.length) {
    return <span className="zustand gruen">aktiv</span>
  }
  const inhalt = (
    <span className="zustand">
      {m.status === 'ONBOARDING' ? 'in Einrichtung'
        : m.status === 'SUSPENDED' ? 'ausgesetzt'
          : m.status === 'CLOSED' ? 'beendet' : m.status}
    </span>
  )
  return fehlt.length
    ? <Tipp text={`Offen: ${fehlt.join(', ')}`} fokus>{inhalt}</Tipp>
    : inhalt
}

function Buchungen({ m, aendern }: {
  m: VerwMandant
  aendern: (daten: Record<string, unknown>) => void
}) {
  return (
    <div className="buchungen">
      <label>
        <input type="checkbox" checked={m.module_dashboard}
               onChange={(e) => aendern({ module_dashboard: e.target.checked })} />
        Reporting
      </label>
      <Tipp text="Das Audit braucht die Bedienerdimension.">
        <label>
          <input type="checkbox" checked={m.module_audit}
                 onChange={(e) => aendern({ module_audit: e.target.checked })} />
          Audit
        </label>
      </Tipp>
      <label>
        <input type="checkbox" checked={m.dimension_employee}
               onChange={(e) => aendern({ dimension_employee: e.target.checked })} />
        Personenbezug
      </label>
      <label>
        <input type="checkbox" checked={!!m.dpa_signed_at}
               onChange={(e) => aendern({ dpa_signed: e.target.checked })} />
        AV unterschrieben
      </label>
    </div>
  )
}

function Hochladen({ gewaehlt, fertig }: {
  gewaehlt: VerwMandant | null
  fertig: () => void
}) {
  const feld = useRef<HTMLInputElement>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [erg, setErg] = useState<Hochgeladen | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const schicken = async (datei: File) => {
    setFehler(null); setErg(null); setLaeuft(true)
    try {
      setErg(await api.verwaltung.artikelliste(datei))
      fertig()
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e))
    } finally {
      setLaeuft(false)
      if (feld.current) feld.current.value = ''
    }
  }

  if (!gewaehlt) {
    return (
      <div className="karte">
        <h3>Stammdaten einspielen</h3>
        <p className="dezent">
          Erst einen Kunden wählen — die Datei wird in den gewählten
          Mandanten geladen.
        </p>
      </div>
    )
  }

  return (
    <div className="karte">
      <h3>Stammdaten einspielen</h3>
      <p className="dezent">
        Die Artikelliste aus dem Lightspeed-Backoffice (XLSX) für
        <b> {gewaehlt.name}</b>. Sie sagt, welcher Artikel eine
        Zusammenstellung ist, woraus sie besteht und was Speise oder
        Getränk ist — vor der ersten Buchung.
      </p>

      <input ref={feld} type="file" accept=".xlsx,.csv" disabled={laeuft}
             onChange={(e) => {
               const d = e.target.files?.[0]
               if (d) void schicken(d)
             }} />

      {laeuft && <div className="laedt">Wird gelesen …</div>}
      {fehler && <div className="fehler" role="alert">{fehler}</div>}

      {erg && (
        <div className="ladeergebnis">
          <b>{erg.meldung}</b>
          {!erg.doppelt && (
            <ul>
              <li>{anzahl(erg.artikel)} Artikel, davon {anzahl(erg.neu)} neu</li>
              <li>{anzahl(erg.abfragen)} Abfragen,
                {' '}{anzahl(erg.warengruppen)} Warengruppen</li>
              <li>{anzahl(erg.bestandteile)} Bestandteile</li>
            </ul>
          )}
          {erg.doppelt && (
            <div className="dezent">
              Nichts wurde doppelt geschrieben — die Datei wird an ihrer
              Prüfsumme erkannt.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Anlegen({ fertig }: { fertig: () => void }) {
  const [offen, setOffen] = useState(false)
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)

  if (!offen) {
    return (
      <button className="verweis" onClick={() => setOffen(true)}>
        + Kunden anlegen
      </button>
    )
  }

  const anlegen = async (e: React.FormEvent) => {
    e.preventDefault()
    setFehler(null)
    try {
      await api.verwaltung.anlegen(code, name)
      setOffen(false); setCode(''); setName('')
      fertig()
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <form className="anlegen" onSubmit={anlegen}>
      <input placeholder="Kürzel" value={code} required
             onChange={(e) => setCode(e.target.value)} />
      <input placeholder="Name des Kunden" value={name} required
             onChange={(e) => setName(e.target.value)} />
      <button type="submit">Anlegen</button>
      <button type="button" className="verweis"
              onClick={() => setOffen(false)}>Abbrechen</button>
      {fehler && <div className="fehler" role="alert">{fehler}</div>}
      <p className="dezent">
        Der Kunde steht danach auf „in Einrichtung". Aktiv wird er erst,
        wenn die Auftragsverarbeitung unterschrieben ist — das ist keine
        Einstellung, sondern eine Regel in der Datenbank.
      </p>
    </form>
  )
}

export function Backoffice({ gewaehlterMandant, waehlen }: {
  gewaehlterMandant: string | null
  waehlen: (id: string) => void
}) {
  const [liste, setListe] = useState<VerwMandant[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = () => {
    api.verwaltung.mandanten()
      .then((d) => setListe(d.mandanten))
      .catch((e) => setFehler(e.message))
  }
  useEffect(laden, [])

  const aendern = async (id: string, daten: Record<string, unknown>) => {
    setFehler(null)
    try {
      await api.verwaltung.aendern(id, daten)
      laden()
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e))
    }
  }

  if (fehler && !liste) return <div className="fehler">{fehler}</div>
  if (!liste) return <div className="laedt">Wird geladen …</div>

  const gewaehlt = liste.find((m) => m.id === gewaehlterMandant) ?? null

  return (
    <div className="backoffice">
      {fehler && <div className="fehler" role="alert">{fehler}</div>}

      <div className="karte">
        <div className="karte-kopf">
          <h3>Kunden</h3>
          <Anlegen fertig={laden} />
        </div>

        <table>
          <thead>
            <tr>
              <th>Kunde</th><th>Zustand</th><th>Gebucht</th>
              <th className="r">Standorte</th><th className="r">Dateien</th>
              <th>Letzter Tag</th><th></th>
            </tr>
          </thead>
          <tbody>
            {liste.map((m) => (
              <tr key={m.id} className={m.id === gewaehlterMandant ? 'gewaehlt' : ''}>
                <td>
                  <b>{m.name}</b>
                  <div className="dezent zahl">{m.code}</div>
                </td>
                <td><Zustand m={m} /></td>
                <td><Buchungen m={m} aendern={(d) => aendern(m.id, d)} /></td>
                <td className="r zahl">{anzahl(m.betriebe)}</td>
                <td className="r zahl">{anzahl(m.dateien)}</td>
                <td className="zahl dezent">
                  {m.letzter_tag ? datumKurz(m.letzter_tag) : '—'}
                </td>
                <td>
                  {m.id === gewaehlterMandant
                    ? <span className="dezent">ausgewählt</span>
                    : <button className="verweis"
                              onClick={() => waehlen(m.id)}>auswählen</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="dezent">
          Ausgewählt heißt: Alle Auswertungen zeigen ab jetzt diesen
          Kunden. Es gibt keine Ansicht über mehrere Kunden hinweg — die
          Zeilentrennung in der Datenbank lässt sie gar nicht zu, und das
          ist Absicht.
        </p>
      </div>

      <Hochladen gewaehlt={gewaehlt} fertig={laden} />
    </div>
  )
}
